import { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import {
  expandedFoldersSelector,
  toggleFolderExpanded,
  expandFolder as expandFolderAction,
  expandFolders,
} from 'features/listsNavTreeUI';
import {
  useGetListTreeQuery,
  useMoveListToFolderMutation,
  useUpdateFolderMutation,
  useMoveFolderMutation,
  useCreateFolderMutation,
  useDeleteFolderMutation,
} from 'services/listsTree';
import { useDeleteSharedListMutation, useDuplicateSharedListMutation } from 'services/contacts';

export function useListsNavTree({ slug }) {
  const network = useSelector(activeNetworkSelector);
  const dispatch = useDispatch();

  const expandedFolders = useSelector((state) => expandedFoldersSelector(state, network.id));
  const { data: serverTree, isLoading } = useGetListTreeQuery({ networkId: network.id });
  const match = useRouteMatch(`/networks/${slug}/list/:listId`);

  const [moveListToFolder] = useMoveListToFolderMutation();
  const [updateFolder] = useUpdateFolderMutation();
  const [moveFolder] = useMoveFolderMutation();
  const [createFolder] = useCreateFolderMutation();
  const [deleteFolder, { isLoading: deletingFolder }] = useDeleteFolderMutation();
  const [deleteSharedList, { isLoading: deletingList }] = useDeleteSharedListMutation();
  const [duplicateSharedList, { isLoading: duplicatingList }] = useDuplicateSharedListMutation();

  const toggleFolder = (folderId) => dispatch(toggleFolderExpanded({ networkId: network.id, folderId }));
  const expandFolder = (folderId) => dispatch(expandFolderAction({ networkId: network.id, folderId }));

  const parentIndex = useMemo(() => {
    const map = {};
    const visited = new Set();
    const walk = (nodes, parentId = null, level = 0) => {
      nodes.forEach((node) => {
        const key = `${node.type}:${node.id}`;
        if (visited.has(key)) {
          return;
        }
        visited.add(key);
        map[key] = { parentId, level, type: node.type, id: node.id };
        if (node.type === 'folder' && Array.isArray(node.children)) {
          walk(node.children, node.id, level + 1);
        }
        visited.delete(key);
      });
    };
    walk(serverTree || [], null, 0);
    return map;
  }, [serverTree]);

  const selectedListId = useMemo(() => {
    const id = Number(match?.params?.listId);
    return Number.isNaN(id) ? null : id;
  }, [match?.params?.listId]);

  const activeFolderIds = useMemo(() => {
    if (!selectedListId) return new Set();
    const set = new Set();
    let current = parentIndex[`list:${selectedListId}`]?.parentId ?? null;
    while (current != null) {
      set.add(current);
      current = parentIndex[`folder:${current}`]?.parentId ?? null;
    }
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListId]);

  useEffect(() => {
    if (activeFolderIds.size === 0) return;
    dispatch(expandFolders({ networkId: network.id, folderIds: activeFolderIds }));
  }, [activeFolderIds, dispatch, network.id]);

  return {
    network,
    expandedFolders,
    serverTree,
    isLoading,
    toggleFolder,
    parentIndex,
    expandFolder,
    moveListToFolder,
    moveFolder,
    updateFolder,
    createFolder,
    deleteSharedList,
    deletingList,
    duplicateSharedList,
    duplicatingList,
    deleteFolder,
    deletingFolder,
    selectedListId,
    activeFolderIds,
  };
}

export default useListsNavTree;
